/**
 * RequestAccessModal - Modal for creating permission requests for Andes tables
 * @module components/starnexus/andes/RequestAccessModal
 */

import React, { useState, useEffect, useMemo } from 'react';
import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Button from '@amzn/awsui-components-console/button';
import FormField from '@amzn/awsui-components-console/form-field';
import Multiselect from '@amzn/awsui-components-console/multiselect';
import Select from '@amzn/awsui-components-console/select';
import Input from '@amzn/awsui-components-console/input';
import Textarea from '@amzn/awsui-components-console/textarea';
import Alert from '@amzn/awsui-components-console/alert';
import type { MultiselectProps } from '@amzn/awsui-components-console/multiselect';
import type { SelectProps } from '@amzn/awsui-components-console/select';
import type { IdentityType, CreatePermissionRequestInput, BusinessJustificationType } from '../../../types/starnexus/andesAccess';
import { IDENTITY_TYPE_HELPERS as identityHelpers } from '../../../types/starnexus/andesAccess';
import { mockDatasets } from '../../../data/starnexus/mockDatasets';

const MIN_JUSTIFICATION_LENGTH = 50;

const IDENTITY_TYPE_OPTIONS: SelectProps.Option[] = [
  { label: 'Datanet DB User', value: 'DATANET_DBUSER' },
  { label: 'IAM Role', value: 'IAM_ROLE' },
  { label: 'Abacus Team', value: 'TEAM' },
  { label: 'Cradle Account', value: 'CRADLE_ACCOUNT' }
];

const BUSINESS_JUSTIFICATION_OPTIONS: SelectProps.Option[] = [
  { label: 'Provide Amazon services for repeatable interactions', value: 'Provide Amazon services for repeatable interactions' },
  { label: 'Provide Amazon services for customers to view online', value: 'Provide Amazons services for customers to view online' },
  { label: 'Provide other Amazon services', value: 'Provide other Amazon services' },
  { label: 'Improve Amazon Services - Non-Pseudonymized data', value: 'Improve Amazon Services - Non-Pseudonymized data' },
  { label: 'Improve Amazon Services - Pseudonymized data', value: 'Improve Amazon Services - Pseudonymized data' },
  { label: 'Other', value: 'Other' }
];

export interface RequestAccessModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSubmit: (input: CreatePermissionRequestInput) => Promise<void>;
  preselectedDatasetId?: string;
}

export const RequestAccessModal: React.FC<RequestAccessModalProps> = ({ visible, onDismiss, onSubmit, preselectedDatasetId }) => {
  // Form state
  const [selectedDatasets, setSelectedDatasets] = useState<MultiselectProps.Option[]>([]);
  const [identityType, setIdentityType] = useState<SelectProps.Option | null>(null);
  const [identityValue, setIdentityValue] = useState('');
  const [businessJustificationType, setBusinessJustificationType] = useState<SelectProps.Option | null>(null);
  const [businessJustification, setBusinessJustification] = useState('');

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get datasets with Andes configuration
  const datasetsWithAndes = useMemo(() => {
    return mockDatasets.filter((d) => d.config?.andesSettings?.provider && d.config?.andesSettings?.tableName);
  }, []);

  // Convert datasets to multiselect options
  const datasetOptions: MultiselectProps.Option[] = useMemo(() => {
    return datasetsWithAndes.map((d) => ({
      label: d.datasetName,
      value: d.datasetId,
      description: `${d.config?.andesSettings?.provider} / ${d.config?.andesSettings?.tableName}`
    }));
  }, [datasetsWithAndes]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      setError(null);
      setSubmitting(false);

      // Pre-select dataset if provided
      if (preselectedDatasetId) {
        const dataset = datasetsWithAndes.find((d) => d.datasetId === preselectedDatasetId);
        if (dataset) {
          setSelectedDatasets([
            {
              label: dataset.datasetName,
              value: dataset.datasetId,
              description: `${dataset.config?.andesSettings?.provider} / ${dataset.config?.andesSettings?.tableName}`
            }
          ]);
        }
      } else {
        setSelectedDatasets([]);
      }

      setIdentityType(null);
      setIdentityValue('');
      setBusinessJustificationType(null);
      setBusinessJustification('');
    }
  }, [visible, preselectedDatasetId, datasetsWithAndes]);

  // Get identity type helper info
  const currentIdentityHelper = useMemo(() => {
    if (!identityType?.value) return null;
    return identityHelpers[identityType.value as IdentityType];
  }, [identityType]);

  // Validation
  const identityValueError = useMemo(() => {
    if (!identityValue || !currentIdentityHelper) return '';
    if (!currentIdentityHelper.validation.test(identityValue)) {
      return `Invalid format. Example: ${currentIdentityHelper.example}`;
    }
    return '';
  }, [identityValue, currentIdentityHelper]);

  const justificationError = useMemo(() => {
    if (!businessJustification) return '';
    if (businessJustification.length < MIN_JUSTIFICATION_LENGTH) {
      return `Minimum ${MIN_JUSTIFICATION_LENGTH} characters required (${businessJustification.length}/${MIN_JUSTIFICATION_LENGTH})`;
    }
    return '';
  }, [businessJustification]);

  const isFormValid = useMemo(() => {
    return (
      selectedDatasets.length > 0 &&
      identityType !== null &&
      identityValue.trim() !== '' &&
      !identityValueError &&
      businessJustificationType !== null &&
      businessJustification.length >= MIN_JUSTIFICATION_LENGTH
    );
  }, [selectedDatasets, identityType, identityValue, identityValueError, businessJustificationType, businessJustification]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!isFormValid || !identityType || !businessJustificationType) return;

    setSubmitting(true);
    setError(null);

    try {
      const input: CreatePermissionRequestInput = {
        datasetIds: selectedDatasets.map((d) => d.value!),
        identityType: identityType.value as IdentityType,
        identityValue: identityValue.trim(),
        businessJustificationType: businessJustificationType.value as BusinessJustificationType,
        businessJustification: businessJustification.trim()
      };

      await onSubmit(input);
      onDismiss();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create permission request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Request access"
      size="medium"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} loading={submitting} disabled={!isFormValid || submitting}>
              {selectedDatasets.length > 1 ? `Request access (${selectedDatasets.length} tables)` : 'Request access'}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {selectedDatasets.length > 1 && <Alert type="info">You are requesting access for {selectedDatasets.length} tables. A separate request will be created for each table.</Alert>}

        <FormField label="Tables" description="Select one or more datasets with Andes configuration" constraintText={`${datasetOptions.length} datasets available`}>
          <Multiselect
            selectedOptions={selectedDatasets}
            onChange={({ detail }) => setSelectedDatasets(detail.selectedOptions as MultiselectProps.Option[])}
            options={datasetOptions}
            placeholder="Select datasets"
            filteringType="auto"
            tokenLimit={3}
            disabled={submitting}
          />
        </FormField>

        <FormField label="Identity type" description="The type of identity that will access the table">
          <Select
            selectedOption={identityType}
            onChange={({ detail }) => {
              setIdentityType(detail.selectedOption);
              setIdentityValue(''); // Reset value when type changes
            }}
            options={IDENTITY_TYPE_OPTIONS}
            placeholder="Select identity type"
            disabled={submitting}
          />
        </FormField>

        <FormField
          label="Identity value"
          description={currentIdentityHelper?.description || 'Enter the identity value'}
          errorText={identityValueError}
          constraintText={currentIdentityHelper ? `Example: ${currentIdentityHelper.example}` : undefined}
        >
          <Input
            value={identityValue}
            onChange={({ detail }) => setIdentityValue(detail.value)}
            placeholder={currentIdentityHelper?.placeholder || 'Enter identity value'}
            disabled={submitting || !identityType}
          />
        </FormField>

        <FormField label="Business justification type" description="Select the category that best describes your use case">
          <Select
            selectedOption={businessJustificationType}
            onChange={({ detail }) => setBusinessJustificationType(detail.selectedOption)}
            options={BUSINESS_JUSTIFICATION_OPTIONS}
            placeholder="Select justification type"
            disabled={submitting}
          />
        </FormField>

        <FormField
          label="Business justification"
          description="Explain why you need access to this data"
          errorText={justificationError}
          constraintText={`${businessJustification.length}/${MIN_JUSTIFICATION_LENGTH} characters minimum`}
        >
          <Textarea
            value={businessJustification}
            onChange={({ detail }) => setBusinessJustification(detail.value)}
            placeholder="Provide a detailed explanation of your business need for accessing this data..."
            rows={4}
            disabled={submitting}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default RequestAccessModal;
